import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Lock, LockOpen, Plus, Trash2, Save, Loader2, Tag, ListPlus, Info, Building2, ShieldCheck, Eye, Wand2, RotateCcw, History, ChevronUp, ChevronDown } from "lucide-react";

const SCOPE_LABELS = { industry: "all tenants in the industry", new_only: "new tenants only", specific: "a specific tenant" };

const TERM_LABELS = {
  entity: "Customer noun (singular)", entity_plural: "Customer noun (plural)", identifier: "Account identifier",
  amount: "Amount column", nav_accounts: "Accounts menu label", account_noun: "Account noun",
};
const SCOPES = [
  { key: "industry", label: "All tenants in this industry", desc: "Existing + future tenants inherit this immediately. Locked fields are enforced on everyone." },
  { key: "new_only", label: "New tenants only", desc: "Existing tenants are frozen to their current look; only future signups inherit this template." },
  { key: "specific", label: "A specific tenant", desc: "Apply these values to one chosen tenant only." },
];

export default function IndustryTemplates() {
  const [industries, setIndustries] = useState([]);
  const [industry, setIndustry] = useState("");
  const [data, setData] = useState(null);
  const [labelOv, setLabelOv] = useState({});
  const [termOv, setTermOv] = useState({});
  const [customs, setCustoms] = useState([]);
  const [locks, setLocks] = useState({});
  const [scope, setScope] = useState("industry");
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    api.get("/admin/industry-templates").then((r) => {
      setIndustries(r.data.industries || []);
      if (r.data.industries?.length) setIndustry(r.data.industries[0].industry);
    }).catch(() => toast.error("Could not load industries"));
    api.get("/tenants").then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data.tenants || r.data.items || []);
      setTenants(list);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!industry) return;
    setLoading(true);
    api.get(`/admin/industry-templates/${industry}`).then((r) => {
      setData(r.data);
      setLabelOv(Object.fromEntries((r.data.core_fields || []).map((f) => [f.key, r.data.core_fields.find((x) => x.key === f.key)?.label === f.default_label ? "" : f.label])));
      setTermOv(r.data.term_overrides || {});
      setLocks(r.data.locks || {});
      setCustoms((r.data.custom_fields || []).map((c) => ({
        ...c, options: (c.options || []).join(", "), min: c.validation?.min ?? "", max: c.validation?.max ?? "", regex: c.validation?.regex ?? "",
        required: c.required ?? false, visible: c.visible ?? true, searchable: c.searchable ?? false,
        filterable: c.filterable ?? false, sortable: c.sortable ?? false, exportable: c.exportable ?? true, importable: c.importable ?? true,
      })));
    }).catch(() => toast.error("Could not load template")).finally(() => setLoading(false));
  }, [industry]);

  const toggleLock = (key) => setLocks((m) => ({ ...m, [key]: !m[key] }));
  const setLabel = (k, v) => setLabelOv((m) => ({ ...m, [k]: v }));
  const setTerm = (k, v) => setTermOv((m) => ({ ...m, [k]: v }));
  const addCustom = () => setCustoms((c) => [...c, { key: "", label: "", type: "text", options: "", min: "", max: "", regex: "", required: false, visible: true, searchable: false, filterable: false, sortable: false, exportable: true, importable: true }]);
  const setCustom = (i, patch) => setCustoms((c) => c.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeCustom = (i) => setCustoms((c) => c.filter((_, idx) => idx !== i));
  const moveCustom = (i, dir) => setCustoms((c) => {
    const j = i + dir;
    if (j < 0 || j >= c.length) return c;
    const next = [...c]; [next[i], next[j]] = [next[j], next[i]]; return next;
  });

  const industryTenants = tenants.filter((t) => (t.industry || "nbfc") === industry);

  // Effective (unsaved) preview values, mirroring backend resolution.
  const effLabel = (f) => (labelOv[f.key] && labelOv[f.key].trim()) ? labelOv[f.key].trim() : f.default_label;
  const effTerm = (k) => (termOv[k] && termOv[k].trim()) ? termOv[k].trim() : (data?.terms_default?.[k] || k);
  const activeCustoms = customs.filter((c) => (c.label || "").trim());

  const fetchDefaults = () => {
    if (!data) return;
    setTermOv((prev) => {
      const next = { ...prev };
      Object.entries(data.terms_default || {}).forEach(([k, v]) => { if (v) next[k] = v; });
      return next;
    });
    setCustoms((prev) => {
      const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
      const have = new Set(prev.map((c) => slug(c.key || c.label)));
      const additions = (data.industry_custom_fields || [])
        .filter((c) => !have.has(slug(c.key || c.label)))
        .map((c) => ({ key: c.key, label: c.label, type: c.type || "text", options: (c.options || []).join(", "), min: "", max: "", regex: "" }));
      return [...prev, ...additions];
    });
    const n = (data.industry_custom_fields || []).length;
    toast.success(`Loaded ${data.name} default fields${n ? ` — ${n} field(s) added` : ""}. Review and Save.`);
  };

  const save = async () => {
    if (scope === "specific" && !tenantId) { toast.error("Pick a tenant for 'specific tenant' scope"); return; }
    setSaving(true);
    try {
      const clean_labels = Object.fromEntries(Object.entries(labelOv).filter(([, v]) => v && v.trim()));
      const clean_terms = Object.fromEntries(Object.entries(termOv).filter(([, v]) => v && v.trim()));
      const payload_customs = customs.filter((c) => (c.label || "").trim()).map((c, idx) => {
        const out = { key: c.key || undefined, label: c.label.trim(), type: c.type, order: idx };
        if (c.type === "select") out.options = (c.options || "").split(",").map((o) => o.trim()).filter(Boolean);
        const v = {};
        if (c.type === "number" || c.type === "currency") { if (c.min !== "" && c.min != null) v.min = c.min; if (c.max !== "" && c.max != null) v.max = c.max; }
        else if (["text", "textarea", "email", "phone", "date"].includes(c.type)) { if (c.regex) v.regex = c.regex; }
        if (Object.keys(v).length) out.validation = v;
        ["required", "visible", "searchable", "filterable", "sortable", "exportable", "importable"].forEach((fl) => { out[fl] = !!c[fl]; });
        return out;
      });
      const body = { label_overrides: clean_labels, term_overrides: clean_terms, custom_fields: payload_customs, locks, apply_scope: scope };
      if (scope === "specific") body.tenant_id = tenantId;
      const { data: res } = await api.put(`/admin/industry-templates/${industry}`, body);
      if (scope === "new_only") toast.success(`Saved. Froze ${res.frozen_tenants} existing tenant(s); new tenants inherit this.`);
      else if (scope === "specific") toast.success("Applied to the selected tenant.");
      else toast.success(`Saved. ${res.tenants_affected} tenant(s) in this industry now governed by this template.`);
      window.dispatchEvent(new Event("recoveros:config-changed"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save template");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-5xl" data-testid="industry-templates-page">
      <PageHeader title="Industry Field Templates" subtitle="Centrally govern field names, custom fields and what tenants are allowed to edit — per industry.">
        <Button variant="outline" onClick={fetchDefaults} disabled={!data} className="rounded-sm gap-1.5" data-testid="fetch-defaults-btn">
          <Wand2 className="h-4 w-4 text-[#0B3D91]" /> Fetch Industry Default Fields
        </Button>
        <Button variant="outline" onClick={() => setPreviewOpen(true)} disabled={!data} className="rounded-sm gap-1.5" data-testid="preview-as-tenant-btn">
          <Eye className="h-4 w-4 text-[#0B3D91]" /> Preview as Tenant
        </Button>
        <Button onClick={save} disabled={saving || !data} className="rounded-sm gap-1.5 bg-[#0B3D91] hover:bg-[#0a3578]" data-testid="template-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Template
        </Button>
      </PageHeader>

      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Industry</label>
          <Select value={industry} onValueChange={setIndustry}>
            <SelectTrigger className="rounded-sm h-9 w-64" data-testid="industry-select"><SelectValue placeholder="Select industry" /></SelectTrigger>
            <SelectContent>
              {industries.map((i) => <SelectItem key={i.industry} value={i.industry} data-testid={`industry-opt-${i.industry}`}>{i.name} · {i.tenant_count} tenant(s){i.customized ? " ·★" : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {data && <div className="flex items-center gap-1.5 text-sm text-muted-foreground"><Building2 className="h-4 w-4" /> {data.tenant_count} tenant(s) in {data.name}</div>}
      </div>

      <div className="flex items-start gap-2 text-xs text-muted-foreground bg-secondary/50 border border-border rounded-sm px-3 py-2 mb-5">
        <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-[#0B3D91]" />
        <span><b>Lock</b> = tenants cannot rename that field (you control it centrally). Unlocked = tenants may relabel. Renaming is display-only; financial field <b>types</b> stay frozen so Dashboard, Money Stuck Analyzer and aging buckets are never affected.</span>
      </div>

      {data?.updated_at && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground bg-card border border-border rounded-sm px-3 py-2 mb-5" data-testid="template-history">
          <History className="h-3.5 w-3.5 text-[#0B3D91]" />
          Last changed by <b>{data.updated_by_name || "—"}</b> · {new Date(data.updated_at).toLocaleString()}{data.last_scope ? <> · applied to {SCOPE_LABELS[data.last_scope] || data.last_scope}</> : null}
        </div>
      )}

      {loading || !data ? (
        <div className="flex items-center gap-2 text-muted-foreground py-20 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
      ) : (
        <>
          <Section icon={Tag} title="Terminology" testId="tmpl-terms-section">
            <div className="space-y-2">
              {data.term_keys.map((k) => (
                <LockRow key={k} label={<span className="inline-flex items-center gap-2">{TERM_LABELS[k] || k}<SourceBadge source={data.term_sources?.[k]} /></span>} locked={!!locks[k]} onToggle={() => toggleLock(k)} testId={`term-${k}`}
                  canRevert={!!(termOv[k] && termOv[k].trim())} onRevert={() => setTerm(k, "")}>
                  <Input value={termOv[k] ?? ""} placeholder={data.terms_default[k] || ""} onChange={(e) => setTerm(k, e.target.value)} className="rounded-sm h-9" data-testid={`tmpl-term-${k}`} />
                </LockRow>
              ))}
            </div>
          </Section>

          <Section icon={Tag} title="Standard Field Labels" testId="tmpl-core-section">
            <div className="space-y-2">
              {data.core_fields.map((f) => (
                <LockRow key={f.key} label={<span className="inline-flex items-center gap-2">{f.financial_locked && <ShieldCheck className="h-3 w-3 text-amber-600" title="Financial — type frozen" />}{f.default_label}<SourceBadge source={f.source} /></span>}
                  locked={!!locks[f.key]} onToggle={() => toggleLock(f.key)} testId={`label-${f.key}`}
                  canRevert={!!(labelOv[f.key] && labelOv[f.key].trim())} onRevert={() => setLabel(f.key, "")}>
                  <Input value={labelOv[f.key] ?? ""} placeholder={f.default_label} onChange={(e) => setLabel(f.key, e.target.value)} className="rounded-sm h-9" data-testid={`tmpl-label-${f.key}`} />
                </LockRow>
              ))}
            </div>
          </Section>

          <Section icon={ListPlus} title="Custom Fields" testId="tmpl-custom-section"
            action={<Button variant="outline" size="sm" onClick={addCustom} className="rounded-sm gap-1.5" data-testid="tmpl-add-custom-btn"><Plus className="h-4 w-4" /> Add Field</Button>}>
            {customs.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">No template custom fields. Industry default fields still apply automatically.</p>
            ) : customs.map((c, i) => (
              <div key={i} className="border border-border rounded-sm p-3 mb-2" data-testid={`tmpl-custom-row-${i}`}>
                <div className="grid sm:grid-cols-[auto_1fr_140px_auto_auto] gap-3 items-end">
                  <div className="flex flex-col">
                    <Button type="button" variant="ghost" size="icon" onClick={() => moveCustom(i, -1)} disabled={i === 0} className="h-4 w-7" data-testid={`tmpl-custom-up-${i}`}><ChevronUp className="h-3.5 w-3.5" /></Button>
                    <Button type="button" variant="ghost" size="icon" onClick={() => moveCustom(i, 1)} disabled={i === customs.length - 1} className="h-4 w-7" data-testid={`tmpl-custom-down-${i}`}><ChevronDown className="h-3.5 w-3.5" /></Button>
                  </div>
                  <Field label="Field name"><Input value={c.label} placeholder="e.g. Pledge No" onChange={(e) => setCustom(i, { label: e.target.value })} className="rounded-sm h-9" data-testid={`tmpl-custom-label-${i}`} /></Field>
                  <Field label="Type">
                    <Select value={c.type} onValueChange={(v) => setCustom(i, { type: v })}>
                      <SelectTrigger className="rounded-sm h-9" data-testid={`tmpl-custom-type-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{data.field_types.map((t) => <SelectItem key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Button type="button" variant={c.key && locks[c.key] ? "default" : "outline"} size="sm" onClick={() => c.key && toggleLock(c.key)} disabled={!c.key} className="rounded-sm h-9 gap-1" data-testid={`tmpl-custom-lock-${i}`} title={c.key ? "" : "Save first to lock"}>
                    {c.key && locks[c.key] ? <Lock className="h-3.5 w-3.5" /> : <LockOpen className="h-3.5 w-3.5" />}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => removeCustom(i)} className="text-destructive h-9 w-9" data-testid={`tmpl-custom-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
                {c.type === "select" && <Field label="Options (comma separated)" className="mt-2"><Input value={c.options} onChange={(e) => setCustom(i, { options: e.target.value })} className="rounded-sm h-9" data-testid={`tmpl-custom-options-${i}`} /></Field>}
                {(c.type === "number" || c.type === "currency") && <div className="grid grid-cols-2 gap-3 mt-2"><Field label="Min"><Input type="number" value={c.min} onChange={(e) => setCustom(i, { min: e.target.value })} className="rounded-sm h-9" /></Field><Field label="Max"><Input type="number" value={c.max} onChange={(e) => setCustom(i, { max: e.target.value })} className="rounded-sm h-9" /></Field></div>}
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 pt-3 border-t border-border" data-testid={`tmpl-custom-flags-${i}`}>
                  {["required", "visible", "searchable", "filterable", "sortable", "exportable", "importable"].map((fl) => (
                    <label key={fl} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none">
                      <input type="checkbox" checked={!!c[fl]} onChange={(e) => setCustom(i, { [fl]: e.target.checked })} className="accent-[#0B3D91]" data-testid={`tmpl-custom-${fl}-${i}`} />
                      {fl[0].toUpperCase() + fl.slice(1)}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </Section>

          {/* Apply scope */}
          <Section icon={Info} title="Apply to" testId="tmpl-scope-section">
            <div className="space-y-2">
              {SCOPES.map((s) => (
                <button key={s.key} type="button" onClick={() => setScope(s.key)} data-testid={`scope-${s.key}`}
                  className={`w-full text-left rounded-sm border p-3 transition-colors ${scope === s.key ? "border-[#0B3D91] ring-1 ring-[#0B3D91] bg-[#0B3D91]/5" : "border-border hover:border-[#0B3D91]/40"}`}>
                  <div className="font-semibold text-sm">{s.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{s.desc}</div>
                </button>
              ))}
              {scope === "specific" && (
                <Select value={tenantId} onValueChange={setTenantId}>
                  <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="scope-tenant-select"><SelectValue placeholder="Choose a tenant…" /></SelectTrigger>
                  <SelectContent>
                    {industryTenants.length === 0 ? <SelectItem value="__none" disabled>No tenants in this industry</SelectItem>
                      : industryTenants.map((t) => <SelectItem key={t.tenant_id} value={t.tenant_id}>{t.company_name || t.tenant_id}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          </Section>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} className="rounded-sm gap-1.5 bg-[#0B3D91] hover:bg-[#0a3578]" data-testid="template-save-btn-bottom">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Template
            </Button>
          </div>
        </>
      )}

      {data && (
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-4xl" data-testid="preview-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Eye className="h-4 w-4 text-[#0B3D91]" /> Preview — how {data.name} tenants will see this</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-muted-foreground -mt-2">Unsaved preview. Applying will affect <b>{data.tenant_count}</b> tenant(s) depending on the scope you choose.</p>
            <Tabs defaultValue="accounts" className="mt-1">
              <TabsList className="rounded-sm">
                <TabsTrigger value="accounts" data-testid="preview-tab-accounts" className="rounded-sm">Accounts Table</TabsTrigger>
                <TabsTrigger value="import" data-testid="preview-tab-import" className="rounded-sm">Import Wizard</TabsTrigger>
              </TabsList>

              <TabsContent value="accounts" className="mt-3" data-testid="preview-accounts">
                <div className="border border-border rounded-sm overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary/60 text-left">
                      <tr>
                        {[effTerm("identifier"), effTerm("entity"), effTerm("amount"), "DPD", "Bucket", ...activeCustoms.map((c) => c.label)].map((h, i) => (
                          <th key={i} className="px-3 py-2 font-semibold whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[["LN-1001", "Ravi Kumar", "₹42,000", "35", "31-60"], ["LN-1002", "Sita Devi", "₹1,80,000", "12", "1-30"]].map((row, ri) => (
                        <tr key={ri} className="border-t border-border">
                          {row.map((cell, ci) => <td key={ci} className="px-3 py-2 whitespace-nowrap">{cell}</td>)}
                          {activeCustoms.map((c, ci) => <td key={`c${ci}`} className="px-3 py-2 text-muted-foreground italic">{c.type === "select" ? (c.options?.split(",")[0]?.trim() || "—") : "—"}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-muted-foreground mt-2">The <b>{effTerm("nav_accounts")}</b> menu, page headers and the {effTerm("amount")} / {effTerm("entity")} columns reflect your terminology. Underlying values and calculations are unchanged.</p>
              </TabsContent>

              <TabsContent value="import" className="mt-3" data-testid="preview-import">
                <div className="border border-border rounded-sm divide-y divide-border max-h-[50vh] overflow-y-auto">
                  {data.core_fields.map((f) => (
                    <div key={f.key} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="flex items-center gap-1.5">
                        {f.financial_locked && <ShieldCheck className="h-3 w-3 text-amber-600" />}
                        {locks[f.key] && <Lock className="h-3 w-3 text-[#0B3D91]" />}
                        {effLabel(f)}
                      </span>
                      <span className="text-xs text-muted-foreground">maps to <code className="bg-secondary px-1 rounded">{f.key}</code></span>
                    </div>
                  ))}
                  {activeCustoms.map((c, i) => (
                    <div key={`cf${i}`} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="flex items-center gap-1.5">
                        {c.key && locks[c.key] && <Lock className="h-3 w-3 text-[#0B3D91]" />}
                        {c.label}
                        <Badge variant="outline" className="text-[10px] border-[#0B3D91]/40 text-[#0B3D91]">CUSTOM · {c.type}</Badge>
                      </span>
                      <span className="text-xs text-muted-foreground">stored in custom_fields</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2"><Lock className="inline h-3 w-3 text-[#0B3D91]" /> = tenants can't rename · <ShieldCheck className="inline h-3 w-3 text-amber-600" /> = financial field, type frozen (analytics-safe).</p>
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function SourceBadge({ source }) {
  const map = {
    default: ["Default", "bg-slate-100 text-slate-600"],
    admin: ["Admin", "bg-[#0B3D91]/10 text-[#0B3D91]"],
    tenant: ["Tenant", "bg-emerald-100 text-emerald-700"],
    industry: ["Industry", "bg-amber-100 text-amber-700"],
  };
  const [txt, cls] = map[source] || map.default;
  return <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${cls}`} data-testid={`source-${source || "default"}`}>{txt}</span>;
}

function Section({ icon: Icon, title, action, children, testId }) {
  return (
    <div className="border border-border bg-card p-5 mb-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 font-heading font-bold"><Icon className="h-4 w-4 text-[#0B3D91]" /> {title}</div>
        {action}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return <div className={className}><label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>{children}</div>;
}

function LockRow({ label, locked, onToggle, children, testId, canRevert, onRevert }) {
  return (
    <div className="flex items-end gap-3">
      <div className="flex-1"><label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>{children}</div>
      {canRevert && (
        <Button type="button" variant="ghost" size="sm" onClick={onRevert} className="rounded-sm h-9 gap-1 text-muted-foreground" data-testid={`revert-${testId}`} title="Revert to default">
          <RotateCcw className="h-3.5 w-3.5" /> Revert
        </Button>
      )}
      <Button type="button" variant={locked ? "default" : "outline"} size="sm" onClick={onToggle} className={`rounded-sm h-9 gap-1.5 ${locked ? "bg-[#0B3D91] hover:bg-[#0a3578]" : ""}`} data-testid={`lock-${testId}`}>
        {locked ? <><Lock className="h-3.5 w-3.5" /> Locked</> : <><LockOpen className="h-3.5 w-3.5" /> Unlocked</>}
      </Button>
    </div>
  );
}
